import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Link, Typography } from '@mui/material';

/**
 * La letra chica utilizable de la oferta.
 *
 * El texto es del operador y se transmite literal: acá solo se descarta lo que
 * no sea texto (una respuesta cacheada de un backend anterior al contrato
 * podría traer cualquier cosa).
 */
export function bookingConditionLines(conditions: unknown): string[] {
  return Array.isArray(conditions)
    ? conditions.filter(
      (line): line is string => typeof line === 'string' && Boolean(line.trim()),
    )
    : [];
}

/**
 * Si hay sección de condiciones. Es la MISMA expresión con la que el
 * componente decide no dibujarse: quien pone el título por afuera pregunta acá
 * y no puede quedar un encabezado solo sobre un bloque vacío.
 */
export function hasBookingConditions({
  conditions,
  showFixedPax,
}: {
  conditions?: unknown;
  showFixedPax: boolean;
}): boolean {
  return bookingConditionLines(conditions).length > 0 || showFixedPax;
}

interface Props {
  /** La letra chica que publica la oferta. Texto del operador, tal cual. */
  conditions?: unknown;
  /** Cuántos pasajeros cotizó la búsqueda. */
  passengerCount: number;
  /** Autos: la ocupación no es del producto, así que no se rotula. */
  showFixedPax: boolean;
}

/**
 * Las condiciones de la oferta, en el mismo lugar en los nueve verticales.
 *
 * Plegadas: es información para transmitirle al pasajero, no para decidir —
 * lo que decide (confirmación inmediata, cancelación gratis) sube a la barra
 * como flag. Mismo gesto que en las cards de resultados.
 *
 * El título lo pone quien la ubica en el flujo (`BookingSectionDisclosure`),
 * que es también quien la pliega entera en mobile.
 */
export function BookingConditions({ conditions, passengerCount, showFixedPax }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const lines = bookingConditionLines(conditions);

  if (!hasBookingConditions({ conditions, showFixedPax })) return null;

  return (
    <Box>
      {lines.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" display="block">
            {lines[0]}
          </Typography>
          {lines.length > 1 && (
            <>
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                // En mobile el `caption` de 12px queda por debajo del piso de
                // legibilidad de un control que hay que tocar: sube a 13 solo
                // ahí, sin mover el desktop.
                sx={(theme) => ({
                  [theme.breakpoints.down('sm')]: { fontSize: 13 },
                })}
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded
                  ? t('travelServices.booking.hideConditions')
                  : t('travelServices.booking.showConditions', { count: lines.length })}
              </Link>
              {expanded && (
                <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
                  {lines.slice(1).map((line, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="caption"
                      color="text.secondary"
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </>
      )}
      {/* La ocupación es la cotizada: cambiarla exige re-buscar. */}
      {showFixedPax && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('travelServices.booking.fixedPax', { count: passengerCount })}
        </Typography>
      )}
    </Box>
  );
}
